import { useMemo, useState } from "react";
import type { ComponentDiagnostic } from "@swot/base-components";
import { Alert, Button, Card, Input, LoadingState } from "@swot/base-components";
import type { AuthRepository } from "@cmts/auth/authRepository.js";
import type { QuickAccessUser } from "@services/cmtsLoginApi.js";
import type { CmtsHostRuntime } from "../../app/appRuntime.js";
import { runtimeModeLabel } from "../../app/runtimeMode.js";
import { requireCmtsUiAction } from "@cmts/utils/cmtsActionGuards.js";
import { CmtsQuickAccessModal } from "@components/auth/index.js";
import { normalizeUserSafeApiError } from "@cmts/api/apiErrorNormalizer.js";

type LoginState = {
  loginId: string;
  password: string;
  tenantCode: string;
  error?: string | undefined;
  quickAccessError?: string | undefined;
  submitting: boolean;
  modalOpen: boolean;
  quickAccessLoading: boolean;
  quickAccessUsers: readonly QuickAccessUser[];
};

const initialLoginState = (tenantCode?: string): LoginState => ({
  loginId: "",
  password: "",
  tenantCode: tenantCode ?? "",
  submitting: false,
  modalOpen: false,
  quickAccessLoading: false,
  quickAccessUsers: [],
});

function safeErrorMessage(error: unknown, fallback: string): string {
  return normalizeUserSafeApiError(error).userMessage || fallback;
}

function normalizedTenantCode(value: string, fallback: string | undefined): string {
  return (value.trim() || fallback || "").trim();
}

export function CmtsLoginPage(props: {
  repository?: AuthRepository | undefined;
  runtime: CmtsHostRuntime;
  diagnostics?: readonly ComponentDiagnostic[] | undefined;
  onLoginSuccess?: (() => void | Promise<void>) | undefined;
}) {
  const [state, setState] = useState<LoginState>(() => initialLoginState(props.runtime.config.tenantCode));
  const runtimeLabel = runtimeModeLabel(props.runtime.config.resolvedRuntimeMode);
  const isProduction = props.runtime.config.appEnvironment === "production";
  const configuredDefaultTenantCode = props.runtime.config.tenantCode?.trim();
  const tenantCodePlaceholder = configuredDefaultTenantCode ? `Example: ${configuredDefaultTenantCode}` : "Example: tenant-lub";
  const securityNotice = useMemo(
    () =>
      isProduction
        ? "Quick Access is disabled in production unless explicitly enabled by backend policy."
        : "Quick Access loads eligible login users from the configured CMTS backend for the selected tenant code. Password entry remains required unless backend policy explicitly supports safe non-production seed credentials.",
    [isProduction],
  );

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (state.submitting) return;
    if (!props.repository) {
      setState((s) => ({ ...s, error: "Auth repository is not configured." }));
      return;
    }
    const loginId = state.loginId.trim();
    const password = state.password;
    const tenantCode = normalizedTenantCode(state.tenantCode, configuredDefaultTenantCode);
    if (!tenantCode) {
      setState((s) => ({ ...s, error: "Tenant Code is required." }));
      return;
    }
    if (!loginId) {
      setState((s) => ({ ...s, error: "Login ID is required." }));
      return;
    }
    if (!password) {
      setState((s) => ({ ...s, error: "Password is required." }));
      return;
    }
    setState((s) => ({ ...s, submitting: true, error: undefined }));
    try {
      await props.repository.login({ loginId, password, tenantCode });
      await requireCmtsUiAction(props.onLoginSuccess, "onLoginSuccess")();
    } catch (error) {
      setState((s) => ({
        ...s,
        error: safeErrorMessage(error, "Unable to sign in. Check Tenant Code, Login ID, credentials and backend availability."),
      }));
    } finally {
      setState((s) => ({ ...s, submitting: false }));
    }
  }

  async function openQuickAccess() {
    if (isProduction) {
      setState((s) => ({ ...s, error: "Quick Access is disabled in production unless backend policy explicitly permits it." }));
      return;
    }
    if (!props.repository) {
      setState((s) => ({ ...s, error: "Auth repository is not configured." }));
      return;
    }
    const tenantCode = normalizedTenantCode(state.tenantCode, configuredDefaultTenantCode);
    if (!tenantCode) {
      setState((s) => ({ ...s, error: "Enter a Tenant Code before opening Quick Access." }));
      return;
    }
    setState((s) => ({ ...s, tenantCode, modalOpen: true, quickAccessLoading: true, quickAccessUsers: [], quickAccessError: undefined, error: undefined }));
    try {
      const users = await props.repository.quickAccessUsers(tenantCode);
      setState((s) => ({ ...s, quickAccessUsers: users, quickAccessLoading: false }));
    } catch (error) {
      setState((s) => ({
        ...s,
        quickAccessLoading: false,
        quickAccessError: safeErrorMessage(error, "Unable to load Quick Access users from backend."),
      }));
    }
  }

  function selectQuickAccessUser(user: QuickAccessUser) {
    if (!user.isActive || !user.canLogin) {
      setState((s) => ({ ...s, quickAccessError: "Selected user is inactive or is not allowed to login." }));
      return;
    }
    setState((s) => ({
      ...s,
      loginId: user.loginId,
      tenantCode: user.tenantCode || s.tenantCode,
      modalOpen: false,
      quickAccessError: undefined,
      error: undefined,
    }));
  }

  return (
    <main className="cmts-login-page" aria-label="CMTS login">
      <section className="cmts-login-hero">
        <div className="cmts-brand-mark">
          <img src="/logo.png" alt="SWOT Logo" className="cmts-login-logo" />
        </div>
        <p className="cmts-eyebrow">Enterprise PWA · Complaint Management and Tracking</p>
        <h1>SWOT DigiNXT CMTS</h1>
        <p className="cmts-login-lead">
          A professional complaint workflow cockpit for registration, SLA, evidence, RCA, CAPA,
          verification, escalation, audit and management reporting.
        </p>
        <div className="cmts-login-highlights" aria-label="CMTS capabilities">
          <span>Workflow-driven</span>
          <span>RBAC / SoD</span>
          <span>SLA-aware</span>
          <span>Evidence-safe</span>
          <span>Audit-ready</span>
          <span>PWA-enabled</span>
        </div>
        <div className="cmts-powered-by">
          <span>Powered by</span>
          <img src="/Atribslogowhite.jpeg" alt="Atribs Logo" className="cmts-powered-logo" />
        </div>
      </section>
      <section className="cmts-login-panel">
        <Card title="Sign in to CMTS" subtitle="Secure tenant-aware access">
          {state.error ? <Alert title="Login blocked" tone="danger">{state.error}</Alert> : null}
       <form
  className="cmts-login-form"
  onSubmit={submit}
  aria-label="CMTS tenant-code login form"
  noValidate
>
  <Input
    label="Tenant Code"
    placeholder={tenantCodePlaceholder}
    value={state.tenantCode}
    onChange={(event) =>
      setState((current) => ({
        ...current,
        tenantCode: event.target.value,
      }))
    }
    autoComplete="organization"
    required
  />

  <Input
    label="Login ID"
    value={state.loginId}
    onChange={(event) =>
      setState((current) => ({
        ...current,
        loginId: event.target.value,
      }))
    }
    autoComplete="username"
    required
  />

  <Input
    label="Password"
    value={state.password}
    onChange={(event) =>
      setState((current) => ({
        ...current,
        password: event.target.value,
      }))
    }
    type="password"
    autoComplete="current-password"
    required
  />
    <Button className="cmts-primary-button" intent="submit" disabled={state.submitting}>
    {state.submitting ? "Signing in..." : "Login"}
  </Button>
</form> 
          <div className="cmts-login-actions">
            <Button
              className="cmts-secondary-button"
              intent="button"
              onClick={() => void openQuickAccess()}
              disabled={state.quickAccessLoading || isProduction || state.submitting}
            >
              {state.quickAccessLoading ? "Loading users..." : "Quick Access"}
            </Button>
            <span className="cmts-login-note">
              Version - BETA - PWA ready - Network-aware - {runtimeLabel}
            </span>
          </div>
          <p className="cmts-login-note">{securityNotice}</p>
        </Card>
      </section>
      <CmtsQuickAccessModal
        open={state.modalOpen}
        loading={state.quickAccessLoading}
        users={state.quickAccessUsers}
        securityNotice={securityNotice}
        error={state.quickAccessError}
        onClose={() => setState((s) => ({ ...s, modalOpen: false, quickAccessError: undefined }))}
        onSelectUser={selectQuickAccessUser}
      />
    </main>
  );
}
